import { createSelector } from "reselect"
import { get, chain, map, filter, orderBy, find, first, capitalize, size, isNumber, pick } from "lodash"
import { getTickerAndMicFromContributor } from "helpers/securities"
import { getGrouping } from "helpers/compositeSecurities"
import { getAllAvailableRollups, getRollupSummaryLabel } from "helpers/rollups"
import { computeGroupedContributorsCSV, computeContributorsGroupCSV, computeContributorsCSV } from "helpers/csv"
import { numberFormatter, DEFAULT_CURRENCY } from "helpers/formatters/numbers"
import { contributorsSummaryTextElem, combineDuplicateContributors, getDollarExposure } from "helpers/contributors"
import { computeSelectedGroup } from "helpers/analyze"
import { createQueryStringSelector } from "selectors/queryString"

const EMPTY_ARRAY = []
const EMPTY_OBJECT = {}

const getSelectedGroupId = createQueryStringSelector("contributorGroup")
const getPositionSetName = (_, props) => props.positionSetName
const getViewType = (_, props) => props.viewType
const getContributorsDataRaw = state => get(state, "global.data.compositeConcentrationContributors.data.model.simulation.composition.0.positions")
const getGroupedContributorsData = state => get(state, "global.data.compositeGroupedConcentrationContributors.data.model.simulation.composition.0.compositionBy")
const getRawContributors = state => get(state, "global.data.compositeConcentrationContributors.data.model.simulation.composition.0")
const getContributorsCurrency = state => get(state, "global.data.compositeConcentrationContributors.data.model.simulation.currency")
const getGroupedContributorsCurrency = state => get(state, "global.data.compositeGroupedConcentrationContributors.data.model.simulation.currency")
const getOrder = (_, props) => get(props, "sortOrder", EMPTY_OBJECT)
const getClassifications = (_, props) => props.classifications
const getSelectedDate = (_, props) => props.selectedDate
const getCurrency = (_, props) => props.currency
const getRawGroupingId = createQueryStringSelector("grouping")
const getRawGroupingType = createQueryStringSelector("groupingType")
export const getSelectedContributorCategory = (_, props) => props.selectedContributorCategory

const getGroupedContributorsWithName = createSelector(
  [getGroupedContributorsData],
  (groupedContributors) => {
    return map(groupedContributors, contributor => ({
      ...contributor,
      name: contributor.description,
    }))
  },
)
export const getSelectedGroup = createSelector(
  [getSelectedGroupId, getGroupedContributorsWithName],
  (selectedGroupId, groupedContributorsData) => {
    if (!selectedGroupId) {
      return null
    }

    const result = computeSelectedGroup({ id: selectedGroupId }, groupedContributorsData)
    if (result) {
      result.totalPercentEquity /= 100
    }

    return result
  },
)

const getContributorsEquity = createSelector(
  [getRawContributors],
  (contributors) => {
    const { referenceEquity, gmv } = contributors
    return referenceEquity || gmv
  },
)

const getContributorsDisparity = createSelector(
  [getRawContributors],
  (contributors) => {
    const { positionsCount, positions } = contributors
    return positionsCount !== size(positions)
  },
)

// This normally would be a getSelectedField selector but in this case,
// it never changes and you cant actually get the right key from the items(getItems)
const SELECTED_FIELD = "size"

const getUniqueContributors = createSelector(
  [getContributorsDataRaw],
  (contributors) => combineDuplicateContributors(contributors, true),
)

const getContributors = createSelector(
  [getUniqueContributors, getContributorsEquity],
  (data, equity) => map(data, security => {
    const { ticker, mic: exchange } = getTickerAndMicFromContributor(security)
    return {
      id: security.id,
      modelProviderId: security.modelProviderId,
      country: security.country,
      description: security.description,
      classification: get(security, "classification.name"),
      currency: security.currency,
      assetClass: security.assetClass,
      assetSubclass: security.assetSubclass,
      size: security.economicExposure / 100,
      economicExposure: getDollarExposure(security.economicExposure, equity),
      bloombergTicker: security.bloombergTicker,
      factors: {
        maxConcentration: numberFormatter(security.economicExposure * equity),
      },
      ticker,
      exchange,
    }
  }),
)

const getFilteredContributors = createSelector(
  [getContributors],
  (data) => filter(data, security => get(security, SELECTED_FIELD) != null),
)

export const getSortedContributors = createSelector(
  [getFilteredContributors, getOrder],
  (data, order) => {
    if (data.length === 0) return EMPTY_ARRAY

    return orderBy(data, [
      (security) => {
        switch (order.by) {
          case "name":
            return security.id
          case "economicExposure":
            return order.abs ? Math.abs(security.size) : security.size
          default: {
            const value = get(security, SELECTED_FIELD)
            return order.abs && isNumber(value) ? Math.abs(value) : value
          }
        }
      },
    ], [order.dir])
  },
)

const groupContributors = ({ data, equity }) => map(data, group => ({
  ...group,
  contributors: map(combineDuplicateContributors(group.contributors), security => {
    const { ticker, mic: exchange } = getTickerAndMicFromContributor(security)
    return {
      ...security,
      size: security.economicExposure / 100,
      ticker,
      exchange,
      economicExposure: getDollarExposure(security.economicExposure, equity),
    }
  }),
  name: group.description,
  totalPercentEquity: group.totalPercentEquity / 100,
  economicExposure: getDollarExposure(group.totalPercentEquity, equity),
}))

const getGroupedContributors = createSelector(
  [getGroupedContributorsData, getContributorsEquity],
  (data, equity) => groupContributors({ data, equity }),
)

const getFilteredGroupedContributors = createSelector(
  [getSelectedGroup, getGroupedContributors],
  (selectedGroup, contributors) => filter(contributors, group => !selectedGroup || selectedGroup.id === group.id),
)

export const getSortedGroupContributors = createSelector(
  [getFilteredGroupedContributors, getOrder],
  (data, order) => {
    if (data.length === 0) return EMPTY_ARRAY

    return chain(data)
      .map(group => ({
        ...group,
        value: get(group, SELECTED_FIELD),
        contributors: orderBy(group.contributors, [
          (security) => {
            switch (order.by) {
              case "name":
                return security.id
              case "economicExposure":
                return order.abs ? Math.abs(security.size) : security.size
              default: {
                const value = get(security, SELECTED_FIELD)
                return order.abs && isNumber(value) ? Math.abs(value) : value
              }
            }
          },
        ], [order.dir]),
      }))
      .orderBy([
        (group) => {
          switch (order.by) {
            case "name":
              return group.description
            default:
              return order.abs ? Math.abs(group.totalPercentEquity) : group.totalPercentEquity
          }
        },
      ], [order.dir])
      .value()
  },
)

export const getAvailableRollups = createSelector(
  [getClassifications, getSelectedDate],
  (classifications, date) => {
    const accountRollups = {
      classifications,
    }
    return getAllAvailableRollups(accountRollups, date)
  },
)

export const getCurrentGrouping = createSelector(
  [getRawGroupingId, getRawGroupingType, getAvailableRollups],
  getGrouping,
)

export const getContributorsSummaryText = createSelector(
  [getCurrentGrouping, getSortedContributors, getSortedGroupContributors, getSelectedGroup, getContributorsEquity, getRawContributors, getContributorsDisparity, getCurrency],
  (contributorsGrouping, contributors, groups, selectedGroup, equity, rawComposition, positionsDisparity, currency) => contributorsSummaryTextElem({
    contributorsGrouping,
    contributors,
    groups,
    selectedGroup,
    equity,
    ...pick(rawComposition, ["referenceEquity", "gmv"]),
    positionsDisparity,
    currency,
  }))

const getCsvCurrencyCode = createSelector(
  [getCurrentGrouping, getSelectedGroup, getContributorsCurrency, getGroupedContributorsCurrency],
  (contributorsGrouping, selectedGroup, contributorsCurrency, groupedCurrency) => {
    const responseCurrency = contributorsGrouping || selectedGroup ? groupedCurrency : contributorsCurrency
    return responseCurrency || DEFAULT_CURRENCY
  },
)

export const getCsvData = createSelector(
  [getSortedContributors, getSortedGroupContributors, getSelectedContributorCategory, getCurrentGrouping, getPositionSetName, getSelectedDate, getViewType, getSelectedGroup, getCsvCurrencyCode],
  (data, groupedData, contributorCategory, contributorsGrouping, portfolioName, selectedDate, viewType, selectedGroup, currency) => {
    if (selectedGroup) {
      const group = first(groupedData)
      return {
        ...computeGroupedContributorsCSV({
          type: "Economic Exposure",
          portfolioName,
          selected: null,
          selectedDate,
          viewType,
          group,
          net: true,
          showEconomicExposure: true,
          currency,
        }),
        fileName: `${portfolioName} ${capitalize(viewType)} Concentration Grouped By ${group.name} ${selectedDate}`,
      }
    }

    if (contributorsGrouping) {
      return {
        ...computeContributorsGroupCSV({
          type: "Economic Exposure",
          portfolioName,
          selected: null,
          selectedDate,
          viewType,
          contributorsGrouping,
          data: groupedData,
          net: true,
          showEconomicExposure: true,
          currency,
        }),
        fileName: `${portfolioName} ${capitalize(viewType)} Concentration Grouped By ${getRollupSummaryLabel(contributorsGrouping)} ${selectedDate}`,
      }
    }

    return {
      ...computeContributorsCSV({
        type: "Economic Exposure",
        portfolioName,
        selected: null,
        selectedDate,
        viewType,
        data,
        net: true,
        showEconomicExposure: true,
        currency,
      }),
      fileName: `${portfolioName} ${capitalize(viewType)} Concentration ${selectedDate}`,
    }
  },
)

export const getSelectedGroupValue = createSelector(
  [getSelectedGroup, getGroupedContributors],
  (selectedGroup, groupedContributors) => {
    if (selectedGroup) {
      const group = find(groupedContributors, { id: selectedGroup.id })
      return get(group, SELECTED_FIELD)
    }

    return null
  },
)

export const hasReferenceEquity = createSelector([getRawContributors],
  (contributors) => get(contributors, "referenceEquity", null) !== null,
)

export const getSelectedGroupSiblings = createSelector(
  [getSelectedGroup, getGroupedContributors],
  (selectedGroup, groupedContributors) => {
    if (selectedGroup) {
      return chain(groupedContributors)
        .orderBy(["name"], ["asc"])
        .value()
    }

    return null
  },
)
